"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useRegionStore } from "@/store/index";
import { swapCountryInPath, countryFromPath } from "@/lib/util/country";

/**
 * Keeps the /{countryCode} URL segment in sync with the user's stored
 * selection (the source of truth). If they disagree, silently rewrite the URL
 * to match — no banner, no user action. Replaces the old CountryCodeValidator,
 * which only nagged the user to fix the URL themselves.
 *
 * `countryCode` reads null until the persisted store rehydrates; the selector
 * re-renders this component when hydration fills it in, so we need no separate
 * hydration flag — a null country simply means "nothing to heal yet". Only
 * heals store -> URL; a visitor with no stored country is handled by
 * LocationGuard (which forces the selection modal).
 */
export function CountryCodeSync() {
  const router = useRouter();
  const pathname = usePathname();
  const storedCountryCode = useRegionStore((s) => s.countryCode);

  useEffect(() => {
    if (!storedCountryCode) return;

    const urlCountry = countryFromPath(pathname);
    if (urlCountry === storedCountryCode.toLowerCase()) return;

    // Read the query directly (client-only effect) to preserve deep-link params
    // without pulling useSearchParams into a layout-level component.
    const query = window.location.search;
    router.replace(swapCountryInPath(pathname, storedCountryCode) + query);
  }, [storedCountryCode, pathname, router]);

  return null;
}
